/**
 * Website entry point.
 *
 * This file is the entry point for the website and is responsible for importing
 * the necessary assets, initializing the application, and fetching the application
 * docket from the API.
 *
 * @since 2.0.0
 * @version 3.0.0
 * @module index
 */

/**
 * Import JavaScript Assets
 * @since 3.0.0
 */
import { Cookies } from "commonAssets/cookies";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { Header } from "commonAssets/components/Header";
import { Navigation } from "commonAssets/components/Navigation";
import { Footer } from "commonAssets/components/Footer";
import { ImportantDates } from "commonAssets/components/ImportantDates";
import { CustomPage } from "./scripts/customPage";
import { siteTitle } from "commonAssets/lib/siteTitle.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { I18n } from "commonAssets/i18n";
import {
    loading,
    appDocketAPI,
    appClaimForm,
    siteInfoLang,
    userLanguage,
    setSiteInfo,
    setSiteInfoLang,
    setEmailInform,
    setPhoneAdmin,
    setEmailConfirm,
    setUserLanguage,
    setAppLocale,
    setI18n,
} from "commonAssets/js/constants";
import languages from "./languages";

/**
 * Import Styles
 * @since 3.2.3
 */
import "./style/custom.css";

/**
 * Import Custom JavaScript Script
 * @since 3.0.0
 */
import {
    customInitClaim,
    customProcessClaim,
    customLoadMember,
} from "./scripts/custom";
import { customSubmitClaim } from "./scripts/SubmitClaim/index.mjs";
import { jsbmedia } from "./scripts/jsbmedia";

// Initialize error handler
const errorHandler = new ErrorHandler();

/**
 * Internationalization (i18n) Handling
 *
 * Sets the languages available for the distribution.
 *
 * Examples:
 * i18n.languages returns languages in an array
 * i18n.userLanguage returns current languages
 * i18n.getTranslation('string') returns predefined string
 */
const i18n = I18n.create(["en"], {}, languages);
setI18n(() => i18n);

/**
 * Cookies
 * @returns {void}
 */
const set_cookies = new Cookies();
set_cookies.init();

/**
 * Set the user's language using the value from `set_cookies.language()`.
 * @param {Function} setCookiesLanguage - The function that returns the user's language.
 */
setUserLanguage(() => set_cookies.language());

/**
 * Set the application locale using the value from `set_cookies.locale()`.
 * @param {Function} setCookiesLocale - The function that returns the application locale.
 */
setAppLocale(() => set_cookies.locale());

/**
 * Loading GIF
 */
loadingGif(loading, "show");

/**
 * Initialize the analytics by loading the Google Tag Manager script and
 * configuring the dataLayer and gtag.
 */
export function analytics() {
    window.__sxmJsb = window.__sxmJsb || {};

    window.dataLayer = window.dataLayer || [];
    window.gtag =
        window.gtag ||
        function gtag() {
            window.dataLayer.push(arguments);
        };

    const configureGtag = () => {
        window.gtag("js", new Date());

        // GA4
        window.gtag("config", "G-WKXC7VDFFT"); // swatjs
        window.gtag("config", "G-LGF2TN8VN9"); // general
        window.gtag("config", "G-6D24BDCSLY"); // sirius xm radio case
        window.gtag("config", "G-CLJ4PTDS5W"); // jsb 

        // Google Ads
        window.gtag("config", "AW-10839088199"); // JSB Ads

        // Fire case-specific JSB media events.
        jsbmedia();
    };

    if (window.__sxmJsb.gtagScriptLoaded) {
        configureGtag();
        return;
    }

    window.__sxmJsb.gtagScriptLoaded = true;
    $.getScript(
        "https://www.googletagmanager.com/gtag/js?id=G-CLJ4PTDS5W",
        () => configureGtag()
    );
}

/**
 * Calls the appropriate function to display page content based on the given domainSlug.
 * If domainSlug is empty, the 'home' case will be executed.
 *
 * @param {Array} sitePages - An array containing the website sitePages' information.
 * @param {string} domainSlug - The unique identifier (slug) of the page to be displayed.
 * @param {} importantDates -
 *
 * @example
 * // Assuming sitePages array and domainSlug are defined and valid
 * getPage(sitePages, domainSlug, importantDates)
 */
export function getPage(sitePages, domainSlug, importantDates) {
    // Show the loading gif
    loadingGif(loading, "show");

    // Set the site title
    if (domainSlug === "") {
        siteTitle(sitePages);
    } else {
        siteTitle(sitePages, domainSlug);
    }

    // Misc handling.
    domainSlug = domainSlug || "home";

    // Create a new page instance
    const page = new CustomPage(sitePages, domainSlug, importantDates);
    switch (domainSlug) {
        case "home":
            page.home();
            analytics();
            break;

        case "demo":
            page.default();
            analytics();
            break;

        case "submit-claim":
            const submit_claim = customSubmitClaim.createInstance({
                appClaimForm,
                userLanguage,
                customInitClaim,
                customProcessClaim,
                customLoadMember,
                errorHandler,
            });
            submit_claim.form();
            analytics();
            break;

        case "important-documents":
            page.important_documents();
            analytics();
            break;

        case "faqs":
            page.faqs();
            analytics();
            break;

        case "contact-us":
            page.contact();
            analytics();
            break;

        case "en":
            window.location.href = "/?language=en";
            break;

        case "es":
            window.location.href = "/?language=es";
            break;

        default:
            page.error();
            analytics();
    }
}

/**
 * Fetches the application docket from the API and updates the site information.
 *
 * This function performs the following steps:
 * 1. Fetches data from the `appDocketAPI`.
 * 2. Checks the response status and handles errors if the status is greater than 299.
 * 3. Parses the response JSON data.
 * 4. Updates site information such as language, email, phone, and title.
 * 5. Initializes and updates the header, navigation, footer, and important dates on the page.
 *
 * @async
 * @function fetchAppDocket
 * @throws {Error} Throws an error if the API response status is greater than 299 or if there is an error during the fetch operation.
 */

async function fetchAppDocket() {
    try {
        // Fetch data from the app docket API
        const response = await fetch(appDocketAPI);

        // Check the response status and handle errors
        if (response.status > 299) {
            // This error will init the catch block
            throw new Error("App Docket API Error", { cause: response });
        }

        // Parse the response JSON data
        const data = await response.json();

        // Update site information
        setSiteInfoLang(() => data.findResult.lang[userLanguage]);
        setSiteInfo(() => data.findResult);
        setEmailInform(() => data.findResult.email_info);
        setPhoneAdmin(() => data.findResult.phone);
        setEmailConfirm(() => data.findResult.email_conf);

        // Update the document title
        document.title = siteInfoLang.SiteTitle;

        // Domain slug and site pages info
        const domainSlug = window.location.pathname.split("/")[1];
        const sitePages = siteInfoLang.Pages;

        // Deficiency portal: rename "Submit Claim" to "Fix a Claim" (drives the
        // nav tab, page heading, and document title).
        const submitClaimPage = sitePages.find(
            (page) => page.PageName === "submit-claim"
        );
        if (submitClaimPage) {
            submitClaimPage.PageTitle = "Fix a Claim";
        }

        // Initialize the header, footer, and important dates
        const header = new Header(
            siteInfoLang.SiteName,
            siteInfoLang.CaseNumber,
            siteInfoLang.CourtName
        );
        const importantDates = new ImportantDates(data);
        const navigation = new Navigation(sitePages, domainSlug, getPage);
        const footer = new Footer();

        // Header and navigation
        const headerElem = document.querySelector("header");
        headerElem.classList.add("py-3");
        headerElem.appendChild(header.createHeader());

        // Navigation
        const nav_html = navigation.element.outerHTML;
        navigation.pathChange(sitePages, domainSlug, importantDates);

        document
            .querySelector("header")
            .insertAdjacentHTML("afterend", nav_html);
        document.querySelector("footer").appendChild(footer.element);

        // Get the page content
        getPage(sitePages, domainSlug, importantDates);
    } catch (error) {
        // Log and handle errors
        errorHandler.log_exception(
            new Error("Error fetching app docket", { cause: error })
        );
        errorHandler.display(error);

        // Hide the loading gif
        loadingGif(loading, "hide", 1000);
    }
}

// Fetch the application docket when the DOM content is loaded
document.addEventListener("DOMContentLoaded", fetchAppDocket);
